import React, {useEffect, useState} from 'react';
import {
   Button,
   Checkbox,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   List,
   ListItem,
   ListItemIcon,
   ListItemText
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import {useAtom} from "jotai";
import {rangeColumnsSettingsAtom} from "./RangeListTypes";
import {clearCustomColumns, hasCustomColumns, saveCustomColumns} from "./RangeColumnPersistence";

interface RangeCustomizerProps {
   globalId: string;
}

export const RangeCustomizer: React.FC<RangeCustomizerProps> = ({globalId}) => {
   const [open, setOpen] = useState(false);
   const [columnsSettings, setColumnsSettings] = useAtom(rangeColumnsSettingsAtom(globalId));
   const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
   const [isCustomized, setIsCustomized] = useState(false);

   // Check if columns are customized
   useEffect(() => {
      setIsCustomized(hasCustomColumns(globalId));
   }, [globalId, columnsSettings.columnsVisible]);

   // Initialize selectedColumns when dialog opens
   useEffect(() => {
      if (open) {
         // If columnsVisible is defined, use it, otherwise show all columns
         if (columnsSettings.columnsVisible) {
            setSelectedColumns([...columnsSettings.columnsVisible]);
         } else {
            // If no columnsVisible is set, assume all columns are visible
            setSelectedColumns(columnsSettings.columns.map(col => col.id));
         }
      }
   }, [open, columnsSettings.columns, columnsSettings.columnsVisible]);

   const handleOpen = () => {
      setOpen(true);
   };

   const handleClose = () => setOpen(false);

   const handleToggle = (columnId: string) => {
      const currentIndex = selectedColumns.indexOf(columnId);
      const newSelected = [...selectedColumns];

      if (currentIndex === -1) {
         newSelected.push(columnId);
      } else {
         // Ensure at least one column remains selected
         if (newSelected.length > 1) {
            newSelected.splice(currentIndex, 1);
         }
      }

      setSelectedColumns(newSelected);
   };

   const handleApply = () => {
      // Save to localStorage
      saveCustomColumns(globalId, selectedColumns);

      // Update the columnsSettings with the new columnsVisible
      setColumnsSettings(prev => ({
         ...prev,
         columnsVisible: selectedColumns
      }));

      setIsCustomized(true);
      handleClose();
   };

   const handleReset = () => {
      // Clear persisted columns
      clearCustomColumns(globalId);

      // Reset to tab's default columns or all columns if no default
      const defaultColumns = columnsSettings.defaultColumnsVisible
         ?? columnsSettings.columns.map(col => col.id);
      setSelectedColumns(defaultColumns);

      // Update the columnsSettings to use the default
      setColumnsSettings(prev => ({
         ...prev,
         columnsVisible: prev.defaultColumnsVisible
      }));

      setIsCustomized(false);
      handleClose();
   };

   return (
      <>
         <Button
            variant="outlined"
            size="small"
            onClick={handleOpen}
            sx={{
               borderColor: '#d0d0d0',
               height: '100%',
               minWidth: 'auto',
               padding: '11px 2px',
            }}
            aria-label="Customize columns"
         >
            <ViewColumnIcon/>
         </Button>
         <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
         >
            <DialogTitle>Customize Columns</DialogTitle>
            <DialogContent dividers>
               <List>
                  {columnsSettings.columns.map((column) => (
                     <ListItem
                        key={column.id}
                        onClick={() => handleToggle(column.id)}
                        sx={{
                           cursor: 'pointer',
                           '&:hover': {
                              backgroundColor: 'action.hover'
                           }
                        }}
                     >
                        <ListItemIcon>
                           <Checkbox
                              edge="start"
                              checked={selectedColumns.includes(column.id)}
                              tabIndex={-1}
                              disableRipple
                           />
                        </ListItemIcon>
                        <ListItemText primary={column.label}/>
                     </ListItem>
                  ))}
               </List>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
               <div>
                  {isCustomized && (
                     <Button
                        onClick={handleReset}
                     >
                        Reset
                     </Button>
                  )}
               </div>
               <div>
                  <Button onClick={handleClose} sx={{ mr: 1 }}>
                     Cancel
                  </Button>
                  <Button
                     onClick={handleApply}
                     variant="contained"
                     color="primary"
                     disabled={selectedColumns.length === 0}
                  >
                     Apply
                  </Button>
               </div>
            </DialogActions>
         </Dialog>
      </>
   );
};

export default RangeCustomizer;
